'use client';

import { SignIn, SignUp, useAuth } from '@clerk/clerk-react';
import { shadcn } from '@clerk/themes';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { Navigation } from '@/components/layouts/Navigation';
import { Skeleton } from '@/components/ui';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { seoHead } from '@/lib/seo/routeMeta';
import { theme } from '@/lib/ui/theme';

import { Route as rootRoute } from './__root';

// No validateSearch: Clerk appends __clerk_ticket + __clerk_status and the
// <SignUp>/<SignIn> components read the ticket straight from window.location, so there's
// nothing for this route to type. The router's plain-string search serializer keeps these
// params unquoted (it no longer JSON-wraps strings in %22…%22), so they read cleanly.
export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invitation',
  head: () => seoHead('/accept-invitation'),
  component: AcceptInvitationPage,
});

// clerkAppearance mirrors the sign-in / onboarding Clerk widget styling.
const clerkAppearance = {
  baseTheme: shadcn,
  variables: { colorPrimary: '#8b5cf6' },
  elements: { formButtonPrimary: { color: 'white' } },
} as const;

// readClerkParam reads a Clerk-appended query param straight from the URL.
function readClerkParam(name: string): string {
  if (typeof window === 'undefined') return '';
  return (new URLSearchParams(window.location.search).get(name) ?? '').trim();
}

function AcceptInvitationPage() {
  const { isLoaded } = useAuth();
  const navigate = useNavigate();

  const status = readClerkParam('__clerk_status');
  const hasTicket = readClerkParam('__clerk_ticket') !== '';

  // Already-accepted (status=complete) or a stray visit with no ticket → there's
  // nothing to consume; send them into the app (the dashboard's auth guard handles
  // an unauthenticated visitor). Invited members join an already-onboarded organization, so
  // they land on /dashboard — never onboarding/payment.
  useEffect(() => {
    if (status === 'complete' || !hasTicket) {
      navigate({ to: '/dashboard' });
    }
  }, [status, hasTicket, navigate]);

  // Existing Clerk users accept via <SignIn> (ticket strategy); new users via
  // <SignUp>. Both auto-consume __clerk_ticket from the URL in clerk-react v5.
  const showSignIn = status === 'sign_in';
  const showWidget = isLoaded && hasTicket && status !== 'complete';

  return (
    <div>
      <Navigation variant="solid" />
      <main className="relative flex min-h-screen items-center justify-center pt-16">
        <div
          className="absolute inset-0 -z-10 transition-opacity"
          style={{ background: theme.gradient.hero.dark }}
        />
        <div
          className="absolute inset-0 -z-10 opacity-0 transition-opacity dark:opacity-100"
          style={{ background: theme.gradient.hero.light }}
        />
        {showWidget ? (
          showSignIn ? (
            <SignIn
              routing="hash"
              forceRedirectUrl="/dashboard"
              appearance={clerkAppearance}
            />
          ) : (
            <SignUp
              routing="hash"
              forceRedirectUrl="/dashboard"
              appearance={clerkAppearance}
            />
          )
        ) : (
          // Skeleton while Clerk loads / we redirect — avoids a flash of the wrong UI.
          <Skeleton className="h-[28rem] w-[25rem] rounded-2xl" />
        )}
      </main>
      <ThemeToggle />
    </div>
  );
}
